import { HoverIcon, PropertyIcon } from './IconBar';
import type { HoverActionIconProps, PropertyIconProps } from 'owa-mail-list-item-shared';
/* eslint-disable-next-line @typescript-eslint/no-restricted-imports  -- (https://aka.ms/OWALintWiki)
 * Baseline. Do not copy and paste"
 *	> '../index' import is restricted from being used. */
import { lazyGetFlagContextMenu } from '../index';
import { DirectionalHint } from '@fluentui/react/lib/Callout';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { getDensityModeString } from 'owa-fabric-theme';
import { resetFocus } from 'owa-mail-focus-manager';
import { hideMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';
import getPropertyIcon from 'owa-mail-list-actions/lib/utils/conversationProperty/getPropertyIcon';
import PropertyIcons from 'owa-mail-list-actions/lib/utils/conversationProperty/PropertyIcons';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { lazyToggleRowReadState } from 'owa-mail-mark-read-actions';
import { getMoveToPropertiesForContextMenu } from 'owa-mail-movetofolder-view';
import { lazyToggleRowPinnedState, lazyToggleRowsFlagState } from 'owa-mail-triage-action';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { type SmimeType } from 'smime-types';
import getIconForSmimeType from 'owa-smime/lib/utils/getIconForSmimeType';
import { getBigHoverAction } from 'owa-surface-actions/lib/utils/getBigHoverAction';
import React from 'react';
import {
    archiveRow,
    deleteRow,
    useFlagCompleteHelper,
    addPropertyIconToIconBar,
    getHoverActionIcon,
    doesFolderOrViewSupportHoverAction,
    findIconKeyIntersectionSet,
    shouldHideHoverIconWhenIconBarRight,
} from 'owa-mail-list-item-shared';
import type { IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import {
    getMailMenuItemShouldShow,
    doesUserHaveSharedFolderPermissionFor,
} from 'owa-mail-filterable-menu-behavior';
import { type MenuItemType } from 'owa-filterable-menu/lib/components/MenuItemType';
import type { HoverActionKey } from 'owa-outlook-service-option-store';
import type ImportanceType from 'owa-service/lib/contract/ImportanceType';
import { startAnimationOrHandleActionAsNeeded } from 'owa-mail-list-item-animation-store';
import {
    lazyFetchShowSettingsHoverAction,
    lazySaveShowSettingsHoverAction,
    getHoverSurfaceAction,
    getStore as getHoverSurfaceActionStore,
    setShowSettingsHoverActionMutator,
} from 'owa-surface-actions-option';
import getMailboxInfo from 'owa-mail-mailboxinfo/lib/getMailboxInfo';

// Remark: We have to share css file with MailListItem since we need to trigger child hover selectors
// off of the MailListItem container css class
import {
    hoverActionIcon,
    hoverActionIcon_dep,
    icon,
    iconBarContainer,
    iconFull,
    iconDisplay,
    iconNoDisplay,
    alwaysShowPropertyIcons,
    iconHide,
    propertyIcon,
    iconContainerNew,
    iconBarLeftMargin,
    iconInIconBar,
    applyDefaultIconColor,
    full,
    iconBarNoLeftMargin,
} from 'owa-mail-listitem-styles/lib/scss/IconBar.scss';
import {
    mailListItemIsPinned,
    pinIcon,
    pinFilledColor,
    visibilityHidden,
} from 'owa-mail-listitem-styles/lib/scss/MailListItemIconStyles.scss';
import { flagIcon, flagFilledColor } from 'owa-mail-listitem-styles/lib/scss/FlagIconStyles.scss';
import { isFeatureEnabled } from 'owa-feature-flags';
import { lazyMountAndShowFullOptions } from 'owa-options-view';
import { isReadingPanePositionRight } from 'owa-mail-layout/lib/selectors/readingPanePosition';

const hoverActionSource = 'Hover';

export interface MailListItemIconBarProps {
    supportsHoverIcons?: boolean;
    neverShowHoverIcons?: boolean;
    neverShowPropertyIcons?: boolean;
    tableViewId: string;
    lastDeliveryTimestamp: string;
    rowKey: string;
    isRowExpanded: boolean;
    isFlagged: boolean;
    isComplete: boolean;
    isPinned: boolean;
    isInNotesFolder: boolean;
    isInArchiveFolder: boolean;
    isInOutboxFolder: boolean;
    canDelete: boolean;
    unreadCount: number;
    canArchive: boolean;
    canPin: boolean;
    itemClassIcon: PropertyIcons;
    importance: ImportanceType;
    smimeType: SmimeType;
    hasAttachment: boolean;
    hasSharepointLink: boolean;
    effectiveMentioned: boolean;
    isSnoozed: boolean;
    isTaggedForBigScreen: boolean;
    isSingleLine: boolean;
    supportsFlagging: boolean;
    supportsMove: boolean;
    supportsPinning: boolean;
    isReminderSet: boolean;
    containerClassName?: string;
}

/*
Renders the mail list icon bar for both action icons and property icons
*/
const defaultExport: React.FunctionComponent<MailListItemIconBarProps> = observer(
    function MailListItemIconBar(props: MailListItemIconBarProps) {
        const {
            supportsHoverIcons,
            neverShowHoverIcons,
            neverShowPropertyIcons,
            tableViewId,
            rowKey,
            isFlagged,
            isComplete,
            isPinned,
            isInNotesFolder,
            isInArchiveFolder,
            isInOutboxFolder,
            canDelete,
            unreadCount,
            canArchive,
            canPin,
            itemClassIcon,
            importance,
            smimeType,
            hasAttachment,
            hasSharepointLink,
            effectiveMentioned,
            isSnoozed,
            isTaggedForBigScreen,
            isSingleLine,
            supportsFlagging,
            supportsMove,
            supportsPinning,
            isReminderSet,
            containerClassName,
        } = props;

        const tableView = getListViewStore().tableViews.get(tableViewId);
        const mailboxInfo = getMailboxInfo(tableView);
        const densityModeString = getDensityModeString();
        const isReadingPaneRight = isReadingPanePositionRight();
        const isRelocateHoverActionsFlightEnabled = isFeatureEnabled('tri-mlRelocateHoverActions');
        const isIconBarGearButtonEnabled =
            isRelocateHoverActionsFlightEnabled && isFeatureEnabled('tri-mlIconBarSettingsButton');
        const shouldShowHoverActionsInReceivedColumn =
            (!isRelocateHoverActionsFlightEnabled || isReadingPaneRight) && isSingleLine;
        const hoverFlagIndexRef = React.useRef(-1);
        const hoverPinIndexRef = React.useRef(-1);
        const propFlagIndexRef = React.useRef(-1);
        const propPinIndexRef = React.useRef(-1);
        const isReminderSupported =
            isFeatureEnabled('notif-emailReminders') &&
            supportsFlagging &&
            isFlagged &&
            !isComplete;
        const [shouldShowSettingsHoverButton, setShouldShowSettingsHoverButton] = React.useState<
            boolean | undefined
        >(() => {
            return (
                isIconBarGearButtonEnabled && getHoverSurfaceActionStore().showSettingsHoverAction
            );
        });

        React.useEffect(() => {
            if (isIconBarGearButtonEnabled && shouldShowSettingsHoverButton === undefined) {
                lazyFetchShowSettingsHoverAction.importAndExecute().then(() => {
                    setShouldShowSettingsHoverButton(
                        getHoverSurfaceActionStore().showSettingsHoverAction
                    );
                });
            }
        }, [isIconBarGearButtonEnabled, shouldShowSettingsHoverButton]);

        const onDismissMoveToMenu = React.useCallback(() => {
            hideMailItemContextMenu();
            resetFocus('MailListMoveMenuDismissed');
        }, []);

        const moveRow = React.useCallback((): IContextualMenuProps | undefined => {
            return getMoveToPropertiesForContextMenu(
                tableViewId,
                mailboxInfo,
                onDismissMoveToMenu,
                hoverActionSource,
                getMailMenuItemShouldShow,
                DirectionalHint.bottomLeftEdge,
                [rowKey]
            );
        }, [tableViewId, onDismissMoveToMenu, rowKey]);

        const toggleMarkAsReadRow = React.useCallback(() => {
            lazyToggleRowReadState.importAndExecute(rowKey, tableViewId, hoverActionSource);
            // Reset focus after the stitch to mark the item as read is raised
            resetFocus('MailListToggleMarkAsRead');
        }, [rowKey, tableViewId]);

        const togglePinnedStateRow = React.useCallback(() => {
            lazyToggleRowPinnedState.importAndExecute(rowKey, tableViewId, hoverActionSource);
            // Reset focus after the stitch to pin the item is raised
            resetFocus('MailListItemIconBarPinned');
        }, [rowKey, tableViewId]);

        const flaggedOnContextMenu = React.useCallback(
            (
                evt:
                    | React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | unknown, MouseEvent>
                    | React.TouchEvent<HTMLAnchorElement | HTMLButtonElement | unknown>
            ) => {
                evt.stopPropagation();
                evt.persist();
                lazyGetFlagContextMenu.importAndExecute(
                    rowKey,
                    tableViewId,
                    '' /* nodeId */,
                    hoverActionSource,
                    evt as React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>
                );
                evt.preventDefault();
            },
            [rowKey, tableViewId]
        );

        const toggleFlaggedStateRow = React.useCallback(() => {
            lazyToggleRowsFlagState.importAndExecute([rowKey], tableViewId, hoverActionSource);
            // Reset focus after the stitch to flag the item is raised
            resetFocus('MailListItemIconBarFlagged');
        }, [rowKey, tableViewId]);

        const shouldHideHoverIconWhenIconBarRightCallback = React.useCallback(
            (hoverIconKey: string) => {
                return shouldHideHoverIconWhenIconBarRight(
                    hoverIconKey,
                    isFlagged,
                    isComplete,
                    isPinned,
                    false /* isItemPart */,
                    isRelocateHoverActionsFlightEnabled
                );
            },
            [isFlagged, isComplete, isPinned, isRelocateHoverActionsFlightEnabled]
        );

        const hoverActionKeys = useComputedValue((): HoverActionKey[] => {
            let hoverSurfaceActionKeys = getHoverSurfaceActionStore().hoverSurfaceActions;
            if (isInNotesFolder || isInOutboxFolder) {
                hoverSurfaceActionKeys = ['Delete'];
            } else if (hoverSurfaceActionKeys === null) {
                hoverSurfaceActionKeys = getHoverSurfaceAction();
            }

            if (!isRelocateHoverActionsFlightEnabled) {
                return hoverSurfaceActionKeys;
            }

            const filteredActionKeys: HoverActionKey[] = [];
            for (const actionKey of hoverSurfaceActionKeys) {
                const key = doesFolderOrViewSupportHoverAction(
                    actionKey,
                    canDelete,
                    canArchive,
                    supportsMove,
                    supportsFlagging,
                    supportsPinning,
                    canPin
                )
                    ? actionKey
                    : 'None';
                filteredActionKeys.push(key);
            }

            if (shouldShowSettingsHoverButton) {
                filteredActionKeys[0] = 'Settings';
            }

            return filteredActionKeys;
        }, [
            shouldShowSettingsHoverButton,
            canDelete,
            canArchive,
            supportsMove,
            supportsFlagging,
            supportsPinning,
            canPin,
            isInNotesFolder,
            isInOutboxFolder,
        ]);

        const flagEntry = useFlagCompleteHelper(
            {
                title: null,
                key: null,
                onClickCommand: toggleFlaggedStateRow,
                onContextMenu: flaggedOnContextMenu,
            },
            isFlagged,
            isComplete
        );
        const bigHoverAction = getBigHoverAction(hoverActionKeys);
        const hoverActionIcons = useComputedValue((): HoverActionIconProps[] => {
            const hoverActions: HoverActionIconProps[] = [];

            if (!isRelocateHoverActionsFlightEnabled) {
                hoverActionKeys.forEach(action => {
                    const hoverActionIconProps = getHoverActionIcon(
                        action,
                        isFlagged,
                        isComplete,
                        isPinned,
                        unreadCount
                    );
                    switch (action) {
                        case 'Archive':
                            if (bigHoverAction !== action && canArchive) {
                                /*We do not want the icon to be shown if already shown in the big hover action.*/
                                hoverActions[0] = {
                                    ...hoverActionIconProps,
                                    onClickCommand: () => {
                                        const handleArchive = () => {
                                            archiveRow(rowKey, tableViewId, hoverActionSource);
                                        };
                                        startAnimationOrHandleActionAsNeeded(
                                            'Archive',
                                            [rowKey],
                                            hoverActionSource,
                                            handleArchive
                                        );
                                    },
                                };
                            }
                            break;
                        case 'Move':
                            const shouldShowMove =
                                supportsMove &&
                                doesUserHaveSharedFolderPermissionFor(29, tableView);

                            if (shouldShowMove) {
                                hoverActions[1] = {
                                    ...hoverActionIconProps,
                                    menuProps: moveRow(),
                                };
                            }
                            break;
                        case 'Delete':
                            if (bigHoverAction !== action && canDelete) {
                                hoverActions[2] = {
                                    ...hoverActionIconProps,
                                    onClickCommand: () =>
                                        deleteRow(rowKey, tableViewId, hoverActionSource),
                                };
                            }
                            break;
                        case 'ReadUnread':
                            hoverActions[3] = {
                                ...hoverActionIconProps,
                                onClickCommand: toggleMarkAsReadRow,
                            };
                            break;
                        case 'FlagUnflag':
                            if (
                                supportsFlagging &&
                                doesUserHaveSharedFolderPermissionFor(
                                    18,
                                    tableView
                                ) /* Shared folder permission check */
                            ) {
                                flagEntry.iconClassName = classnames(flagEntry.iconClassName);
                                hoverActions[4] = flagEntry;
                            }
                            break;
                        case 'PinUnpin':
                            if (
                                supportsPinning &&
                                canPin &&
                                doesUserHaveSharedFolderPermissionFor(
                                    39,
                                    tableView
                                ) /* Shared folder permission check */
                            ) {
                                hoverActions[5] = {
                                    ...hoverActionIconProps,
                                    onClickCommand: togglePinnedStateRow,
                                };
                            }
                            break;
                        case 'None':
                            break;
                    }
                });
            } else {
                hoverFlagIndexRef.current = -1;
                hoverPinIndexRef.current = -1;
                for (let i = 0; i < hoverActionKeys.length; i++) {
                    const action = hoverActionKeys[i];
                    // in SCL, if BigHoverAction is present, shift hover actions to right
                    const iconIndex = !isSingleLine && bigHoverAction != 'None' ? i + 1 : i;
                    const hoverActionIconProps = getHoverActionIcon(
                        action,
                        isFlagged,
                        isComplete,
                        isPinned,
                        unreadCount
                    );
                    if (action == 'Settings') {
                        hoverActions[i] = {
                            ...hoverActionIconProps,
                            onClickCommand: () => {
                                lazySaveShowSettingsHoverAction.importAndExecute().then(() => {
                                    lazyMountAndShowFullOptions
                                        .importAndExecute('mail', 'surfaceActions')
                                        .then(() => {
                                            //ensure that icon bar doesn't rerender until after the settings page is opened
                                            /* eslint-disable-next-line owa-custom-rules/forbid-mutators-inside-loops -- (https://aka.ms/OWALintWiki)
                                             * DO NOT COPY-PASTE!, replace this line with a justification or fix the issue.
                                             *	> Mutator functions should not be called inside loops. Consider moving the mutator call outside the loop or restructuring the code. */
                                            setShowSettingsHoverActionMutator(false);
                                            setShouldShowSettingsHoverButton(false);
                                        });
                                });
                            },
                        };
                    } else if (action == 'Delete' && (isSingleLine || bigHoverAction !== action)) {
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: () => deleteRow(rowKey, tableViewId, hoverActionSource),
                        };
                    } else if (action == 'Archive' && (isSingleLine || bigHoverAction !== action)) {
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: () => {
                                const handleArchive = () => {
                                    archiveRow(rowKey, tableViewId, hoverActionSource);
                                };
                                startAnimationOrHandleActionAsNeeded(
                                    'Archive',
                                    [rowKey],
                                    hoverActionSource,
                                    handleArchive
                                );
                            },
                        };
                    } else if (action == 'Move') {
                        hoverActions[iconIndex] = { ...hoverActionIconProps, menuProps: moveRow() };
                    } else if (action == 'ReadUnread') {
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: toggleMarkAsReadRow,
                        };
                    } else if (action == 'FlagUnflag') {
                        hoverFlagIndexRef.current = iconIndex;
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: toggleFlaggedStateRow,
                            onContextMenu: flaggedOnContextMenu,
                        };
                    } else if (action == 'PinUnpin') {
                        hoverPinIndexRef.current = iconIndex;
                        hoverActions[iconIndex] = {
                            ...hoverActionIconProps,
                            onClickCommand: togglePinnedStateRow,
                        };
                    }
                }
            }

            return hoverActions;
        }, [
            isInNotesFolder,
            isInArchiveFolder,
            isInOutboxFolder,
            rowKey,
            canArchive,
            tableViewId,
            canDelete,
            unreadCount,
            isPinned,
            canPin,
            supportsMove,
            supportsFlagging,
            supportsPinning,
            isFlagged,
            isComplete,
            flagEntry,
            isSingleLine,
            hoverActionKeys,
        ]);

        const propertyIcons = useComputedValue((): PropertyIconProps[] => {
            if (isRelocateHoverActionsFlightEnabled) {
                propFlagIndexRef.current = -1;
                propPinIndexRef.current = -1;
                // for SCL we show 3 icons b/c delete is bigHoverAction
                const MAX_PROPERTY_ICONS = 4;
                const propertyIconProps: PropertyIconProps[] = Array.from(
                    { length: MAX_PROPERTY_ICONS },
                    (_, index) => ({
                        key: `placeholder-${index}`,
                        iconName: '',
                    })
                );
                const spotsToFill = Array.from({ length: MAX_PROPERTY_ICONS }, () => false);
                const remainingSpots = { value: MAX_PROPERTY_ICONS };

                // Flag or Complete icon
                const addFlagPropertyIconIfSupported = () => {
                    if (supportsFlagging && (isFlagged || isComplete)) {
                        let flagPropertyIcon: PropertyIconProps = {
                            iconName: 'None',
                            key: 'flagIcon',
                        };
                        if (isFlagged && !isComplete) {
                            flagPropertyIcon = {
                                key: 'flagIcon',
                                iconClasses: flagFilledColor,
                                iconName: getPropertyIcon(PropertyIcons.Flagged).iconName,
                            };
                        } else if (isComplete) {
                            flagPropertyIcon = {
                                key: 'completeIcon',
                                iconName: getPropertyIcon(PropertyIcons.Completed).iconName,
                            };
                        }

                        addPropertyIconToIconBar(
                            propertyIconProps,
                            spotsToFill,
                            remainingSpots,
                            flagPropertyIcon,
                            hoverFlagIndexRef.current
                        );
                    }
                };

                // Pin icon if pinned
                const addPinPropertyIconIfSupported = () => {
                    if (supportsPinning && isPinned) {
                        addPropertyIconToIconBar(
                            propertyIconProps,
                            spotsToFill,
                            remainingSpots,
                            {
                                key: 'pinIcon',
                                iconClasses: classnames(
                                    pinFilledColor,
                                    (isFlagged || isComplete) && !isPinned && visibilityHidden
                                ),
                                iconName: getPropertyIcon(PropertyIcons.Pinned).iconName,
                            },
                            hoverPinIndexRef.current
                        );
                    }
                };

                // add flag and pin property icons
                // if either flag or pin hover action is enabled, but not both, prioritize adding the enabled one first
                if (hoverPinIndexRef.current > -1 && hoverFlagIndexRef.current == -1) {
                    addPinPropertyIconIfSupported();
                    addFlagPropertyIconIfSupported();
                } else {
                    addFlagPropertyIconIfSupported();
                    addPinPropertyIconIfSupported();
                }

                // Item class icon first (meeting, note, etc.)
                if (
                    itemClassIcon &&
                    itemClassIcon !== PropertyIcons.None &&
                    remainingSpots.value > 0
                ) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: itemClassIcon.toString(),
                        ...getPropertyIcon(itemClassIcon),
                    });
                }
                // Importance icon
                if (importance == 'Low' && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.ImportanceLow.toString(),
                        ...getPropertyIcon(PropertyIcons.ImportanceLow),
                    });
                } else if (importance == 'High' && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.ImportanceHigh.toString(),
                        ...getPropertyIcon(PropertyIcons.ImportanceHigh),
                    });
                }
                // S/MIME icons
                if (smimeType !== 0 && remainingSpots.value > 0) {
                    const smimeIcon: 'LockClosedRegular' | 'RibbonRegular' =
                        getIconForSmimeType(smimeType);
                    smimeIcon &&
                        addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                            key: smimeIcon,
                            iconName: smimeIcon,
                        });
                }
                // Attachment icon
                if (hasAttachment && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.Attachment.toString(),
                        ...getPropertyIcon(PropertyIcons.Attachment),
                    });
                } else if (hasSharepointLink && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.Link.toString(),
                        ...getPropertyIcon(PropertyIcons.Link),
                    });
                }
                // At mention icon
                if (effectiveMentioned && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.GlobalMentionedMe.toString(),
                        ...getPropertyIcon(PropertyIcons.GlobalMentionedMe),
                        iconClasses: classnames(
                            supportsPinning && isPinned && mailListItemIsPinned
                        ),
                    });
                }
                // If the conversation/item has been snoozed or tagged for big screen, then show the clock icon
                if ((isSnoozed || isTaggedForBigScreen) && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.Snooze.toString(),
                        ...getPropertyIcon(PropertyIcons.Snooze),
                    });
                }

                // Reminder icon
                if (isReminderSupported && isReminderSet && remainingSpots.value > 0) {
                    addPropertyIconToIconBar(propertyIconProps, spotsToFill, remainingSpots, {
                        key: PropertyIcons.Reminder.toString(),
                        ...getPropertyIcon(PropertyIcons.Reminder),
                    });
                }
                // Trim leading property icon placeholders
                let removeCount = 0;
                while (propertyIconProps.length > 0 && propertyIconProps[0].iconName === '') {
                    propertyIconProps.shift();
                    removeCount++;
                }
                // Property Icon indexes for flag and pin icons are based on location in hoverActionIcons array
                // Since we are removed leading placeholders, we need to adjust the indexes by the number of
                // leading placeholders removed
                if (hoverFlagIndexRef.current > -1) {
                    propFlagIndexRef.current = hoverFlagIndexRef.current - removeCount;
                }
                if (hoverPinIndexRef.current > -1) {
                    propPinIndexRef.current = hoverPinIndexRef.current - removeCount;
                }
                return propertyIconProps;
            } else {
                const propertyIconProps: PropertyIconProps[] = [];
                // Build the property icon list from left to right
                // Item class icon first (meeting, note, etc.)
                if (itemClassIcon && itemClassIcon !== PropertyIcons.None) {
                    propertyIconProps.push({
                        key: itemClassIcon.toString(),
                        ...getPropertyIcon(itemClassIcon),
                    });
                }
                // Importance icon
                if (importance == 'Low') {
                    propertyIconProps.push({
                        key: PropertyIcons.ImportanceLow.toString(),
                        ...getPropertyIcon(PropertyIcons.ImportanceLow),
                    });
                } else if (importance == 'High') {
                    propertyIconProps.push({
                        key: PropertyIcons.ImportanceHigh.toString(),
                        ...getPropertyIcon(PropertyIcons.ImportanceHigh),
                    });
                }
                // S/MIME icons
                if (smimeType !== 0) {
                    const smimeIcon: 'LockClosedRegular' | 'RibbonRegular' =
                        getIconForSmimeType(smimeType);
                    smimeIcon &&
                        propertyIconProps.push({
                            key: smimeIcon,
                            iconName: smimeIcon,
                        });
                }
                // Attachment icon
                if (hasAttachment) {
                    propertyIconProps.push({
                        key: PropertyIcons.Attachment.toString(),
                        ...getPropertyIcon(PropertyIcons.Attachment),
                    });
                } else if (hasSharepointLink) {
                    propertyIconProps.push({
                        key: PropertyIcons.Link.toString(),
                        ...getPropertyIcon(PropertyIcons.Link),
                    });
                }
                // At mention icon
                if (effectiveMentioned) {
                    propertyIconProps.push({
                        key: PropertyIcons.GlobalMentionedMe.toString(),
                        ...getPropertyIcon(PropertyIcons.GlobalMentionedMe),
                        iconClasses: classnames(
                            supportsPinning && isPinned && mailListItemIsPinned
                        ),
                    });
                }
                // If the conversation/item has been snoozed or tagged for big screen, then show the clock icon
                if (isSnoozed || isTaggedForBigScreen) {
                    propertyIconProps.push({
                        key: PropertyIcons.Snooze.toString(),
                        ...getPropertyIcon(PropertyIcons.Snooze),
                    });
                }

                // Reminder icon
                if (isReminderSupported && isReminderSet) {
                    propertyIconProps.push({
                        key: PropertyIcons.Reminder.toString(),
                        ...getPropertyIcon(PropertyIcons.Reminder),
                    });
                }

                // Complete icon
                if (supportsFlagging && isComplete) {
                    propertyIconProps.push({
                        key: 'completeIcon',
                        iconName: getPropertyIcon(PropertyIcons.Completed).iconName,
                    });
                }

                // Flag icon
                if (supportsFlagging && isFlagged && !isComplete) {
                    propertyIconProps.push({
                        key: 'flagIcon',
                        iconClasses: flagIcon,
                        iconName: getPropertyIcon(PropertyIcons.Flagged).iconName,
                    });
                }
                // Pin icon if pinned
                // Add flag/complete icon as the right most icon if pinning is not supported
                // If pinning is supported we leave a place for pin hover action/property icon
                if (supportsPinning && (isPinned || isFlagged || isComplete)) {
                    propertyIconProps.push({
                        key: 'pinIcon',
                        iconClasses: classnames(
                            pinIcon,
                            (isFlagged || isComplete) && !isPinned && visibilityHidden
                        ),
                        iconName: getPropertyIcon(PropertyIcons.Pinned).iconName,
                    });
                }
                return propertyIconProps;
            }
        }, [
            itemClassIcon,
            importance,
            smimeType,
            hasAttachment,
            hasSharepointLink,
            effectiveMentioned,
            unreadCount,
            isSnoozed,
            isTaggedForBigScreen,
            rowKey,
            isComplete,
            isFlagged,
            isPinned,
            canPin,
            supportsFlagging,
            supportsPinning,
            hoverActionKeys,
            isRelocateHoverActionsFlightEnabled,
            isReminderSet,
        ]);

        const containerClassNames = classnames(
            iconBarContainer,
            containerClassName,
            isRelocateHoverActionsFlightEnabled &&
                (isSingleLine && isReadingPaneRight ? iconBarNoLeftMargin : iconBarLeftMargin)
        );

        // when icons are in both hover actions and property icons, it means that the hover action is enabled and the property associated with that action is true
        // only applicable for flag and pin actions.
        const iconKeyIntersectionSet = useComputedValue(() => {
            return findIconKeyIntersectionSet(
                hoverActionIcons,
                propertyIcons,
                isSingleLine,
                hoverFlagIndexRef.current,
                hoverPinIndexRef.current,
                propFlagIndexRef.current,
                propPinIndexRef.current,
                isRelocateHoverActionsFlightEnabled
            );
        }, [isRelocateHoverActionsFlightEnabled, hoverActionIcons, propertyIcons, isSingleLine]);

        if (!neverShowHoverIcons || !neverShowPropertyIcons) {
            return (
                <div className={containerClassNames}>
                    {!neverShowHoverIcons &&
                        hoverActionIcons.map(hoverProps => {
                            return (
                                <HoverIcon
                                    {...hoverProps}
                                    key={hoverProps.key}
                                    iconClasses={classnames(
                                        hoverProps.iconClasses,
                                        isRelocateHoverActionsFlightEnabled
                                            ? hoverActionIcon
                                            : hoverActionIcon_dep,
                                        // Hide certain icons when the item is not hovered to prevent shifting of pinned/flagged status
                                        shouldShowHoverActionsInReceivedColumn
                                            ? shouldHideHoverIconWhenIconBarRightCallback(
                                                  hoverProps.key
                                              )
                                                ? classnames(iconDisplay, iconHide) // see --iconVisibilityWhenDefaultHidden
                                                : iconKeyIntersectionSet?.has(hoverProps.key) &&
                                                  iconDisplay
                                            : undefined,
                                        icon,
                                        !isRelocateHoverActionsFlightEnabled
                                            ? densityModeString === 'full' && iconFull
                                            : iconContainerNew
                                    )}
                                    densityModeString={densityModeString}
                                />
                            );
                        })}
                    {!neverShowPropertyIcons &&
                        propertyIcons.map(propertyIconProps => {
                            return (
                                <PropertyIcon
                                    {...propertyIconProps}
                                    key={propertyIconProps.key}
                                    iconClasses={classnames(
                                        iconInIconBar,
                                        !propertyIconProps.iconClasses
                                            ? applyDefaultIconColor
                                            : undefined,
                                        densityModeString === 'full' && full,
                                        propertyIconProps.iconClasses,
                                        propertyIcon,
                                        isRelocateHoverActionsFlightEnabled && iconContainerNew,
                                        ((isRelocateHoverActionsFlightEnabled &&
                                            !isReadingPaneRight) ||
                                            !isSingleLine) &&
                                            neverShowHoverIcons &&
                                            alwaysShowPropertyIcons, // In SCL or when RP bottom or Hidden, if neverShowHoverIcons is true, force to always show the property icon.
                                        shouldShowHoverActionsInReceivedColumn
                                            ? iconKeyIntersectionSet?.has(propertyIconProps.key)
                                                ? supportsHoverIcons
                                                    ? iconNoDisplay
                                                    : alwaysShowPropertyIcons
                                                : alwaysShowPropertyIcons
                                            : undefined
                                    )}
                                />
                            );
                        })}
                </div>
            );
        } else {
            return null;
        }
    },
    'MailListItemIconBar'
);
export default defaultExport;
